<template>
  <dpa-id-collab-graphql-provider :endpoint="config.graphqlBase" :token="token">
    <v-app v-if="!shouldRedirectToLogin">
      <slc-app-bar-provider :resize-header="resizeHeader" />
      <v-content
        :class="
          `App u-bgApp ${$route.query.src === 'app' ? 'is-appBarHidden' : ''}`
        "
      >
        <v-container
          fluid
          class="SlcApp-container"
          :class="{
            'is-desktop': $vuetify.breakpoint.mdAndUp,
            isDetailVisible: isDetailVisible
          }"
        >
          <router-view />
          <v-snackbar
            class="AgApp-message mb-2"
            v-model="message.model"
            bottom
            :timeout="message.timeout || 6000"
          >
            {{ message.text }}
          </v-snackbar>
        </v-container>
      </v-content>

      <!--
      EC-603: Pan/scroll overflow gradient indicators for FRC in mobile/table view
    -->
      <div v-if="frcGradientIsTop" class="CdkSearch-frcGradient is-top" />
      <div v-if="frcGradientIsBottom" class="CdkSearch-frcGradient is-bottom" />

      <!--
      NB: The "app" property of v-navigation-drawer would auto-adjust the
      layout of the sidebar. Therefore the navigation drwawer sometimes would be
      shown without an active sidebar.
    -->
      <template v-if="$vuetify.breakpoint.lgAndUp && isSidebarVisible">
        <v-navigation-drawer
          :disable-route-watcher="true"
          :value="isSidebarVisible"
          app
          fixed
          right
          class="AgApp-rightDrawer"
          width="400px"
          :style="`z-index: ${maxZIndex + 50} !important;`"
        >
          <dpa-id-collab-sidebar
            v-show="isSidebarVisible"
            :token="token"
            :folder-id="selectedFolder ? selectedFolder.id : null"
            :collab-origin="config.collabURL"
            :refresh="sidebarRefresh"
            @close="
              closeSidebar();
              resizeHeader();
            "
            @folderChange="setSelectedFolder"
            @itemClick="eventClicked"
          />
        </v-navigation-drawer>
      </template>

      <slc-tos-dialog></slc-tos-dialog>

      <dpa-id-collab-folder-dialog
        v-if="showCollabDialog"
        :mode="collabDialogMode"
        :items="formattedSelectedEvents"
        @close="showCollabDialog = false"
        @itemsAdded="handleCollabDialogSuccess"
        @changeMode="collabDialogMode = $event"
        :style="{ zIndex: `${maxZIndex + 50} !important` }"
      />
      <portal-target name="globalDialog" />
      <slc-no-feed-dialog />
    </v-app>
  </dpa-id-collab-graphql-provider>
</template>

<script>
import Vue, { provide, defineComponent, onMounted, computed } from "vue";
import { mapActions, mapGetters, mapState, mapMutations } from "vuex";
import axios from "axios";
import qs from "query-string";
import {
  DpaIdCollabGraphqlProvider,
  DpaIdCollabSidebar,
  DpaIdCollabFolderDialog
} from "@dpa-connect/dpa-id-collab";
import SlcAppBarProvider from "@/components/SlcAppBar/SlcAppBarProvider.vue";
import SlcTosDialog from "@/components/SlcTosDialog/SlcTosDialog.vue";
import useAuthentication from "@/composition/useAuthentication";
import useWatchAuthentication from "@/composition/useWatchAuthentication";
import maxZIndex from "@/views/lib/useMaxZIndex";
import SlcNoFeedDialog from "@/components/SlcNoFeedDialog.vue";
import { isCookieConsentBannerActive } from "./cookieBannerState";

const eventBus = new Vue();

export default defineComponent({
  name: "App",
  components: {
    DpaIdCollabGraphqlProvider,
    SlcAppBarProvider,
    SlcTosDialog,
    DpaIdCollabSidebar,
    DpaIdCollabFolderDialog,
    SlcNoFeedDialog
  },
  setup() {
    const {
      isAuthenticated,
      validateAndRetrieveToken,
      profile,
      logout
    } = useAuthentication();
    useWatchAuthentication();

    const handleUnauthorizedRequest = ({ status }) => {
      if (status === 401) {
        logout();
      }
    };

    axios.interceptors.response.use(
      response => {
        handleUnauthorizedRequest(response);
        return response;
      },
      error => {
        if (error.response) handleUnauthorizedRequest(error.response);
      }
    );

    provide("eventBus", eventBus);
    const query = qs.parse(window.location.search);
    // If redirected from dpa ID login, read the JTW from the ticket query
    const authToken = validateAndRetrieveToken() || query.ticket || "";

    // show cookie consent banner
    onMounted(() => {
      if (isCookieConsentBannerActive.value) {
        setTimeout(() => {
          // eslint-disable-next-line no-unused-expressions
          window.OneTrust?.ToggleInfoDisplay();
          isCookieConsentBannerActive.value = false;
        }, 3000);
      }
    });

    const ticketAuthState = computed(() => ({
      isAuthenticated: isAuthenticated.value,
      ticket: qs.parse(window.location.search).ticket
    }));

    return {
      eventBus,
      isAuthenticated,
      token: authToken,
      profile,
      maxZIndex,
      ticketAuthState
    };
  },
  computed: {
    ...mapState("ui", {
      isDetailVisible: ({ right }) => right
    }),
    ...mapState("conceptItems", ["areas"]),
    ...mapState("language", ["language"]),
    ...mapState("filterRules", ["valuesMap"]),
    ...mapState("newsItems", {
      isFeedLoading: ({ isLoading }) => isLoading
    }),
    ...mapState("stageconfig", ["config"]),
    ...mapState("collaboration", ["isSidebarVisible", "selectedFolder"]),
    ...mapGetters("collaboration", ["formattedSelectedEvents"]),
    shouldRedirectToLogin() {
      const result =
        this.$route.path !== "/home" &&
        this.$route.path !== "/unauthorized" &&
        !this.isAuthenticated;
      return result;
    },
    confirmPrivacyAndToSConsent() {
      return this.profile.firstName
        ? !!this.profile.termsAndConditions &&
            !!this.profile.dataPrivacyStatement
        : true;
    },
    resizeHeader() {
      return !!this.isSidebarVisible;
    }
  },
  methods: {
    ...mapActions("stageconfig", ["fetchStageConfig"]),
    ...mapActions("language", ["fetchLanguage"]),
    ...mapActions("conceptItems", [
      "fetchAreas",
      "fetchGenres",
      "fetchRessorts"
    ]),
    ...mapMutations("filterRules", ["setValuesMap"]),
    ...mapMutations("collaboration", ["closeSidebar", "setSelectedFolder"]),
    handleMessage({ model, text, timeout = 6000 }) {
      this.message = { model, text, timeout };
    },
    handleFrcGradient({ isTop, isBottom }) {
      this.frcGradientIsTop = isTop;
      this.frcGradientIsBottom = isBottom;
    },
    openCollabDialog(mode) {
      this.showCollabDialog = true;
      this.collabDialogMode = mode;
    },
    handleCollabDialogSuccess(folder) {
      this.$store.commit("collaboration/setSelectedFolder", folder);
      this.$store.commit("collaboration/clearSelection");
      this.$store.commit("collaboration/openSidebar");
      eventBus.$emit("sidebarRefresh");
      eventBus.$emit("appMessage", {
        model: true,
        text: "Zur Mappe hinzugefügt"
      });
    },
    eventClicked({ id, type }) {
      if (type !== "newsitem") return;
      if (this.isSidebarVisible) {
        this.closeSidebar();
      }
      const route = {
        path: `/newsitems/${this.$route.params.viewMode}/${id}`,
        query: this.$route.query
      };
      this.$router.push(route).catch(() => {});
    },
    onSidebarRefresh() {
      this.sidebarRefresh = true;
      this.$nextTick(() => {
        this.sidebarRefresh = false;
      });
    }
  },
  async created() {
    await this.fetchStageConfig();

    eventBus.$on("appMessage", this.handleMessage);
    eventBus.$on("activateFrcGradient", this.handleFrcGradient);
    eventBus.$on("openCollabDialog", this.openCollabDialog);
    eventBus.$on("sidebarRefresh", this.onSidebarRefresh);
  },
  watch: {
    isSidebarVisible() {
      if (this.isSidebarVisible) {
        const newParams = { ...this.$route.params };
        delete newParams.id;
        const newRoute = {
          path: `/newsitems/${this.$route.params.viewMode}`,
          params: newParams,
          query: this.$route.query
        };
        this.$router.push(newRoute).catch(() => {});
      }
    },
    isAuthenticated: {
      handler(authenticated) {
        if (authenticated) {
          this.fetchGenres(this.token);
          this.fetchRessorts(this.token);
          this.fetchLanguage(this.token);
          /*
           * NB: EC-653 Add all areas to the values maps.
           */
          this.fetchAreas(this.token).then(() => {
            const isGermanDisplayName = ({ locale, role }) =>
              locale === "de" &&
              role === "http://cv.iptc.org/newscodes/namerole/display";

            const areas = this.areas
              .filter(({ names }) => names.some(isGermanDisplayName))
              .map(({ id, names }) => ({
                [id]: names
                  .filter(isGermanDisplayName)
                  .map(({ text }) => text)
                  .join("")
              }))
              .reduce((acc, obj) => {
                const key = Object.keys(obj).join();
                const val = obj[key];

                return {
                  ...acc,
                  [key]: val
                };
              }, {});

            this.setValuesMap({
              ...this.valuesMap,
              FILTER_AREA: areas
            });
          });
        }
      },
      immediate: true
    },
    language(lang) {
      this.$i18n.locale = lang;
      axios.defaults.headers.common["Accept-Language"] = lang;
      document.querySelector("html").setAttribute("lang", lang);
    },
    ticketAuthState: {
      handler() {
        if (
          this.ticketAuthState.ticket &&
          this.ticketAuthState.isAuthenticated
        ) {
          const query = qs.parse(window.location.search);
          const { path } = query;
          const replaceRoute = { ...this.$route, ...(path ? { path } : {}) };
          delete replaceRoute.query.ticket;
          this.$router.replace(replaceRoute);
        }
      },
      deep: true,
      immediate: true
    }
  },
  data() {
    return {
      message: {
        model: false,
        text: "",
        timeout: undefined
      },
      frcGradientIsTop: false,
      frcGradientIsBottom: false,
      docRef: null,
      showListModal: false,
      showListCreationModal: false,
      showCollabDialog: false,
      collabDialogMode: "select",
      sidebarRefresh: false
    };
  },
  /**
   * Save a reference and add class to the html element.
   */
  mounted() {
    this.docRef = document.querySelector("html");
    this.docRef.classList.add("SlcApp");

    // inject and create oneTrust cookie consent snippet
    if (this.config.features.enableOneTrustCookieConsentBanner) {
      const cookieLawScript = document.createElement("script");
      cookieLawScript.setAttribute("type", "text/javascript");
      cookieLawScript.setAttribute("charset", "UTF-8");
      cookieLawScript.setAttribute(
        "src",
        "https://cdn.cookielaw.org/scripttemplates/otSDKStub.js"
      );
      cookieLawScript.setAttribute("data-document-language", "true");
      cookieLawScript.setAttribute(
        "data-domain-script",
        "a173d306-1448-46f0-99cf-095d78e1aaca"
      );
      document.head.appendChild(cookieLawScript);

      const oneTrustScript = document.createElement("script");
      oneTrustScript.setAttribute("type", "text/javascript");
      oneTrustScript.setAttribute("src", "/oneTrust.js");
      document.head.appendChild(oneTrustScript);
    }
  },
  /**
   * Remove DOM reference and classes.
   */
  destroyed() {
    this.docRef.classList.remove("SlcApp");
    this.docRef.classList.remove("is-noScroll");
    this.docRef = null;
  }
});
</script>

<style postcss>
@import "@dpa-connect/dpa-id-collab/lib/dpa-id-collab.css";
@import "@dpa-connect/dpa-connect-classification/lib/dpa-connect-classification.css";
@import "./utils/utils-color/index.css";
@import "./utils/utils-transitions.css";
@import "./utils/utils-responsive.css";

.application {
  font-family: "Inter", sans-serif;
}

/**
 * Override of vuetify classes, ugly but the easiest way for the CI-Changes from EC-4160
 */
.headline {
  font-family: "Inter", sans-serif !important;
  font-size: 16px !important;
  font-weight: 500 !important;
  line-height: 22px !important;
}

.subheading {
  font-size: 12px !important;
}

.v-btn--small {
  font-size: 12px !important;
}

.CdkClassification-link {
  font-size: 12px;
}

/** @define CdkSearch; weak */

/*
 * [1] Should be flat.
 * [2] Must not be scrollable.
 * [3] Overwrites inline styles with fixed position.
 * [4] Overwrites default padding of combobox list.
 * NB: can't add custom class, potentially dangerous when upgrading vuetify.
 */

.CdkSearch-combobox {
  box-shadow: none !important; /* 1. */
  position: fixed; /* 2. */
  top: 56px !important;
}

.CdkSearch-combobox.is-compact {
  top: 48px !important;
}

.CdkSearch-combobox .v-list {
  padding: 0; /* 4. */
}

.CdkSearch-combobox.is-mobile {
  background-color: rgb(33, 33, 33, 0.46);
  height: calc(100vh - 56px);
}

.CdkSearch-combobox.is-mobile.is-cloaked .v-list {
  visibility: hidden;
}

.CdkSearch-frcGradient.is-top {
  background-image: linear-gradient(to top, rgba(255, 255, 255, 0), #fff);
  content: "";
  height: 48px;
  pointer-events: none;
  position: fixed;
  top: 56px;
  width: 100%;
  z-index: 10;
}

.CdkSearch-frcGradient.is-bottom {
  background-image: linear-gradient(to bottom, rgba(255, 255, 255, 0), #fff);
  bottom: 0;
  content: "";
  height: 48px;
  pointer-events: none;
  position: fixed;
  width: 100%;
  z-index: 10;
}

/** @define SlcApp */

.SlcApp-container {
  padding: 16px;
}

.SlcApp-container.is-desktop {
  padding: 16px 52px;
}

.SlcApp-container.is-desktop.isDetailVisible {
  padding-right: 32px;
}

/* OneTrust button is replaced by "Cookies" link */
#ot-sdk-btn-floating {
  display: none !important;
}
</style>

<style lang="postcss" scoped>
.App.is-appBarHidden {
  margin-top: -56px;
  padding-bottom: 56px;
}
</style>
